import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { IgSelect, IgTypography } from "@intelligems/ig-theme";
import { MenuItem, SelectChangeEvent } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { DashboardPageName } from "@src/api/types/dashboards";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { checkAnyScope } from "@src/components/LimitAccess/check-scopes";
import { setAnalyticsSubpage } from "@src/store/slices/dashboardFilters";
import { getCustomMetrics } from "@src/store/slices/experienceMetrics/experienceMetrics";
import { selectedExperience } from "@src/store/slices/experiences";
import { mpClickSubpageEntryPoint } from "@src/utils/mixpanel";

const menuLabelsTb = {
  overview: "Key Metrics",
  conversion: "Conversion",
  revenue: "Revenue",
  discounts: "Discounts",
  orders: "Order Distribution",
  profit: "Profit",
  shipping: "Shipping",
  subscriptions: "Subscriptions",
  productGroups: "Products",
  customMetrics: "Custom Metrics",
  dataReconciliation: "Data Reconciliation",
};

const menuLabelsSitewide = {
  profit: "Profit",
};

interface AnalyticsSubpageSelectProps {
  renderLocation: "sitewide" | "experience";
  pageName: DashboardPageName;
}
export const AnalyticsSubpageSelect = ({
  renderLocation,
  pageName,
}: AnalyticsSubpageSelectProps) => {
  const experience = useSelector(selectedExperience);
  const dispatch = useDispatch();

  const subpages = Object.keys(
    renderLocation === "sitewide" ? menuLabelsSitewide : menuLabelsTb
  );
  const experienceCustomMetrics = useSelector(getCustomMetrics);
  const isAdmin = useIsAdmin();
  const hasDataReconciliation = useConfiguration(
    "view_data_reconciliation_analytics"
  )?.value;
  const handleChange = (e: SelectChangeEvent) => {
    dispatch(
      setAnalyticsSubpage({
        key: "pageName",
        value: e.target.value as DashboardPageName,
      })
    );
    if (experience) {
      mpClickSubpageEntryPoint(
        experience.id!,
        "Subpage Select",
        e.target.value
      );
    }
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <IgSelect
      labelId="vd-select-label"
      id="vd-select"
      displayEmpty
      value={pageName}
      onChange={handleChange}
      renderValue={(e: any) => (
        <IgTypography>
          View:{" "}
          <IgTypography
            sx={{
              fontWeight: 600,
            }}
          >
            {renderLocation === "sitewide"
              ? menuLabelsSitewide[e]
              : menuLabelsTb[e]}
          </IgTypography>
        </IgTypography>
      )}
    >
      {subpages
        .filter((page) =>
          experienceCustomMetrics.length === 0 ? page !== "customMetrics" : page
        )
        .filter((page) =>
          !checkAnyScope([isAdmin || false, hasDataReconciliation])
            ? page !== "dataReconciliation"
            : page
        )
        .map((subpage: DashboardPageName) => (
          <MenuItem
            key={subpage}
            value={subpage}
            sx={{
              fontSize: "14px",
            }}
          >
            {renderLocation === "sitewide"
              ? menuLabelsSitewide[subpage]
              : menuLabelsTb[subpage]}
          </MenuItem>
        ))}
    </IgSelect>
  );
};
